import { memo } from "react";
import {
  FrVrfIcon,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import {
  RoleType,
  TcktStsTypePrjPlan,
  TcktStsTypePrjPrgrs,
  TcktType,
} from "../../util/general-code/enums/general-codes";
import { StakeholderList } from "../../pages/project-plan/ProjectPlanPageState";

type Props = {
  planStatusCd?: string;
  executionStatusCd?: string;
  stakeholderList: StakeholderList[];
};

const VRF_BACKGROUND_COLOR = "#ffffff";
const VRF_PRIMARY_COLOR = "#38468c";

export const ProjectPlanStatusPanel = memo((props: Props) => {
  const { planStatusCd, executionStatusCd, stakeholderList } = props;

  const getStatusPanels = () => {
    const initialStatusPanels: StatusPanel[] = [
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd: "00",
        picRoleCd: RoleType.PROJECT_OWNER,
        panelTitle: "Raise Project",
        tooltipContent: "Raise Project",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd:
          TcktStsTypePrjPlan.WAITING_FOR_ASSIGNMENT_REQUIREMENTS_DEFINER,
        picRoleCd: RoleType.DIP_MANAGER,
        panelTitle: "Assign PIC of requirement definition",
        tooltipContent: "Assign PIC of requirement definition",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd: TcktStsTypePrjPlan.ESTIMATION_IN_PROGRESS,
        picRoleCd: RoleType.PIC_OF_IF_RD,
        panelTitle: "Estimate cost",
        tooltipContent: "Estimate cost",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_ESTIMATE_CONFIRMATION,
        picRoleCd: RoleType.DIP_MANAGER,
        panelTitle: "Confirm cost estimation by IE internal",
        tooltipContent: "Confirm cost estimation by IE internal",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd: TcktStsTypePrjPlan.UNDER_OBTAIN_BUDGET,
        picRoleCd: RoleType.PROJECT_OWNER,
        panelTitle: "Secure budget",
        tooltipContent: "Secure budget",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PLAN,
        tcktStsTypeCd:
          TcktStsTypePrjPlan.PROJECT_PLANNING_FINAL_CHECK_IN_PROGRESS,
        picRoleCd: RoleType.DIP_MANAGER,
        panelTitle: "Approve Project plan",
        tooltipContent: "Approve Project plan",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PROGRESS,
        tcktStsTypeCd: TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
        picRoleCd: RoleType.PIC_OF_IF_RD,
        panelTitle: "Define IF requirements",
        tooltipContent: "Define IF requirements",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PROGRESS,
        tcktStsTypeCd: TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
        picRoleCd: RoleType.PROJECT_OWNER,
        panelTitle: "Assign stakeholders",
        tooltipContent: "Assign stakeholders",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PROGRESS,
        tcktStsTypeCd: TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS,
        picRoleCd: RoleType.PIC_OF_IF_RD,
        panelTitle: "Change IF requirements",
        tooltipContent: "Change IF requirements",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PROGRESS,
        tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS,
        picRoleCd: RoleType.PROJECT_OWNER,
        panelTitle: "Develop and deploy IF",
        tooltipContent: "Develop and deploy IF",
      },
      {
        tcktTypeCd: TcktType.PROJECT_PROGRESS,
        tcktStsTypeCd: TcktStsTypePrjPrgrs.PROJECT_COMPLETE,
        picRoleCd: RoleType.PROJECT_OWNER,
        panelTitle: "Complete Project",
        tooltipContent: "Complete Project",
      },
    ];

    let currentstatsIdx: number = 0;
    if (planStatusCd !== undefined && executionStatusCd !== undefined) {
      currentstatsIdx =
        planStatusCd !== TcktStsTypePrjPlan.PROJECT_PLANNED
          ? initialStatusPanels.findIndex(
              (e) => e.tcktStsTypeCd === planStatusCd,
            )
          : [
                TcktStsTypePrjPrgrs.IF_REQUIREMENT_DESIGN,
                TcktStsTypePrjPrgrs.ASSIGNING_STAKEHOLDERS,
              ].includes(executionStatusCd as TcktStsTypePrjPrgrs)
            ? initialStatusPanels.findIndex(
                (e) => e.tcktStsTypeCd === executionStatusCd,
              ) + 4
            : executionStatusCd === TcktStsTypePrjPrgrs.CHANGING_IF_REQUIREMENTS
              ? 8
              : executionStatusCd === TcktStsTypePrjPrgrs.PROJECT_IN_PROGRESS
                ? 9
                : 10;
    }

    return initialStatusPanels.map((panel, idx) => {
      return {
        ...panel,
        panelType:
          idx === currentstatsIdx
            ? ("inprogress" as StatusPanelType)
            : idx < currentstatsIdx
              ? ("complete" as StatusPanelType)
              : ("notstarted" as StatusPanelType),
        pic: stakeholderList.find(
          (stakeholder) => stakeholder.roleCd === panel.picRoleCd,
        )?.userName,
      };
    });
  };

  const statusPanel = (stats: StatusPanel) => {
    const pickDisp = stats.pic ?? "unassigned";
    let fontColor = VRF_PRIMARY_COLOR;
    let bgColor = VRF_BACKGROUND_COLOR;

    switch (stats.panelType) {
      case "complete":
        fontColor = "gray";
        bgColor = "lightgray";
        break;
      case "inprogress":
        fontColor = VRF_BACKGROUND_COLOR;
        bgColor = VRF_PRIMARY_COLOR;
        break;
    }

    return (
      <>
        <FrVrfTextDisplayField
          style={{
            paddingLeft: "10px",
            backgroundColor: `${bgColor}`,
            "--vrf-font-color": `${fontColor}`,
            "--vrf-text-display-field-font-weight": "bold",
            "--vrf-text-display-field-width": "136px",
            "--vrf-text-display-field-min-height": "50px",
          }}
          text={stats.panelTitle}
        />
        <FrVrfTextDisplayField
          style={{
            paddingLeft: "10px",
            backgroundColor: `${bgColor}`,
            "--vrf-font-color": `${fontColor}`,
            "--vrf-text-display-field-width": "136px",
          }}
          text={`PIC: ${pickDisp}`}
        />
      </>
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexFlow: "row",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        {getStatusPanels().map((stats, index) => {
          return (
            <div key={index} style={{ display: "flex" }}>
              <div>
                {index !== 0 ? <FrVrfIcon icon="frIcon:right-arrow" /> : null}
              </div>
              <div>{statusPanel(stats)}</div>
            </div>
          );
        })}
      </div>
    </>
  );
});

type StatusPanelType = "complete" | "inprogress" | "notstarted";

type StatusPanel = {
  tcktTypeCd: string;
  tcktStsTypeCd: string;
  picRoleCd: string;
  panelTitle: string;
  tooltipContent: string;
  panelType?: StatusPanelType;
  pic?: string;
};
